import {
    ExportService,
    ExportFileFormat,
    ExportContext,
    ExportRange,
} from "app/layout/export-overlay";
import { Injectable } from "@angular/core";
import { get } from "lodash-es";
import { CUSTOMER_ROUTE_FREQUENCY_PROPERTIES, CUSTOMER_TERRITORIES_PROPERTIES } from "./reports-route.constants";
import { subRoutesByGroupRequestDto, subRoutesByRoutesRequestDto, subRoutesFrecuencyRequestDto, subRoutesFrecuencyResponseDto, subRoutesTerritoriesResponseDto } from "app/modules/components/reports/customer-route-frequency/dto/sub-route-reports.dto";
import { CustomerRouteFrequencyService } from "app/modules/components/reports/customer-route-frequency/customer-route-frequency.service";
import { SubRouteTypeEnum } from "@albatrosdeveloper/ave-models-package/lib/enums/sub-route-type.enum";
import { IPaginationResultFrequencies } from "app/common/interfaces/pagination-result.interface";
import { CustomerTerritoriesService } from "app/modules/components/reports/customer-territories/customer-territories.service";
import { RoutesTerritoriesService } from "app/modules/components/reports/routes-territories/routes-territories.service";

const daysMap: { [key: number]: string } = {
    1: 'Lunes',
    2: 'Martes',
    3: 'Miércoles',
    4: 'Jueves',
    5: 'Viernes',
    6: 'Sabado',
    7: 'Domingo',
};

@Injectable()
export class ReportsRoutesExport {
    private currentData: any[] = [];

    constructor(private exportService: ExportService,
        private customerRouteFrequencyService: CustomerRouteFrequencyService,
        private customerTerritoriesService: CustomerTerritoriesService,
        private routesTerritoriesService: RoutesTerritoriesService
    ) { }

    setData(data: any[]) {
        this.currentData = data;
    }

    private transformCustomerRouteFrequency(): any[] {

        return this.currentData.flatMap((item: any) => ({
            routeName: get(item, "routeName", ""),
            subRouteName: get(item, "subRouteName", ""),
            responsibleName: get(item, "responsibleName", ""),
            type: get(item, "type", ""),
            function: get(item, "type", ""),
            custmerCode: get(item, "custmerCode", ""),
            custmerName: get(item, "custmerName", ""),
            days: get(item, "days", ""),
        })
        );
    }

    exportCustomerRouteFrequency(format: ExportFileFormat, range: ExportRange, exportId: string, exportTitle: string,
        totalItems: number, dataToFilter: subRoutesFrecuencyRequestDto, properties?) {
        this.exportService
            .task({
                id: exportId,
                title: exportTitle,
                format,
                iterations: 1,
                start: (ctx: ExportContext) => {
                    ctx.processor.localizeItemProperties(
                        exportTitle,
                        properties ?? CUSTOMER_ROUTE_FREQUENCY_PROPERTIES
                    );
                },
                next: async (ctx: ExportContext) => {
                    const exportDataToFilter = {
                        ...dataToFilter,
                        page: range === ExportRange.AllPages ? 1 : dataToFilter.page,
                        limit: range === ExportRange.AllPages ? totalItems : dataToFilter.limit,
                    };
                    
                    this.customerRouteFrequencyService.filterForExport(exportDataToFilter).subscribe({
                        next: (res: IPaginationResultFrequencies<subRoutesFrecuencyResponseDto>) => {
                            const transformedData = (res?.data?.data ?? []).map(item => ({
                                ...item,
                                type:
                                    item.type === SubRouteTypeEnum.URBAN ? 'URBANA' :
                                        item.type === SubRouteTypeEnum.FOREIGN ? 'FORÁNEA' :
                                            item.type,
                                days: (item.frecuencyDays ?? [])
                                    .sort((a, b) => a - b)
                                    .map(day => daysMap[day])
                                    .filter(Boolean)
                                    .join(','),
                            }));

                            this.currentData = transformedData;
                            const rows = this.transformCustomerRouteFrequency();
                            ctx.processor.addToSection(exportTitle, rows);
                            ctx.next();
                        },
                        error: (err) => {
                            throw(err);
                        },
                    })
                },
            })
            .start();
    }

    private transformCustomerTerritories(): any[] {

        return this.currentData.flatMap((item: any) => ({
            routeName: get(item, "routeName", ""),
            custmerCode: get(item, "custmerCode", ""),
            custmerName: get(item, "custmerName", ""),
            companyName: get(item, "companyName", ""),
        })
        );
    }

    exportCustomerTerritories(format: ExportFileFormat, range: ExportRange, exportId: string, exportTitle: string,
        totalItems: number, dataToFilter: subRoutesByGroupRequestDto, properties?) {
        this.exportService
            .task({
                id: exportId,
                title: exportTitle,
                format,
                iterations: 1,
                start: (ctx: ExportContext) => {
                    ctx.processor.localizeItemProperties(
                        exportTitle,
                        properties ?? CUSTOMER_TERRITORIES_PROPERTIES
                    );
                },
                next: async (ctx: ExportContext) => {
                    dataToFilter.page = range === ExportRange.AllPages ? 1 : dataToFilter.page,
                        dataToFilter.limit = range === ExportRange.AllPages ? totalItems : dataToFilter.limit,
                        this.customerTerritoriesService.findAllWithCount(dataToFilter).subscribe({
                            next: (res: IPaginationResultFrequencies<subRoutesFrecuencyResponseDto>) => {
                                const transformedData = (res?.data?.data ?? []).map(item => ({
                                    ...item,
                                    type:
                                        item.type === SubRouteTypeEnum.URBAN ? 'URBANA' :
                                            item.type === SubRouteTypeEnum.FOREIGN ? 'FORÁNEA' :
                                                item.type,
                                    days: (item.frecuencyDays ?? [])
                                        .map(day => daysMap[day])
                                        .filter(Boolean)
                                        .join(','),
                                }));

                                this.currentData = transformedData;
                                const rows = this.transformCustomerTerritories();
                                ctx.processor.addToSection(exportTitle, rows);
                                ctx.next();
                            },
                            error: (err) => {
                                throw(err);
                            },
                        })
                },
            })
            .start();
    }


    private transformRouteTerritories(): any[] {

        return this.currentData.flatMap((item: any) => ({
            routeName: get(item, "routeName", ""),
            subRouteName: get(item, "subRouteName", ""),
            responsibleName: get(item, "responsibleName", ""),
            type: get(item, "type", ""),
            function: get(item, "type", ""),
            areaName: get(item, "areaName", ""),
            plantName: get(item, "plantName", ""),
            companyName: get(item, "companyName", ""),
        })
        );
    }

    exportRouteTerritories(format: ExportFileFormat, range: ExportRange, exportId: string, exportTitle: string,
        totalItems: number, dataToFilter: subRoutesByRoutesRequestDto, properties?) {
        this.exportService
            .task({
                id: exportId,
                title: exportTitle,
                format,
                iterations: 1,
                start: (ctx: ExportContext) => {
                    ctx.processor.localizeItemProperties(
                        exportTitle,
                        properties ?? CUSTOMER_TERRITORIES_PROPERTIES
                    );
                },
                next: async (ctx: ExportContext) => {
                    dataToFilter.page = range === ExportRange.AllPages ? 1 : dataToFilter.page,
                        dataToFilter.limit = range === ExportRange.AllPages ? totalItems : dataToFilter.limit,
                        this.routesTerritoriesService.findAllWithCount(dataToFilter).subscribe({
                            next: (res: IPaginationResultFrequencies<subRoutesTerritoriesResponseDto>) => {
                                const transformedData = (res?.data?.data ?? []).map(item => ({
                                    ...item,
                                    type:
                                        item.type === SubRouteTypeEnum.URBAN ? 'URBANA' :
                                        item.type === SubRouteTypeEnum.FOREIGN ? 'FORÁNEA' :
                                        item.type,
                                }));

                                this.currentData = transformedData;
                                const rows = this.transformRouteTerritories();
                                ctx.processor.addToSection(exportTitle, rows);
                                ctx.next();
                            },
                            error: (err) => {
                                throw(err);
                            },
                        })
                },
            })
            .start();
    }

    canStartTask(exportId: string): boolean {
        if (this.exportService.taskIsActive(exportId)) {
            this.exportService.setVisible(true);
            return false;
        }
        return true;
    }
}